/**
 * Shared formatting for "these N files failed" acknowledgements.
 *
 * The convention was established by `useDownload` (ISSUE-027): ONE modal for a
 * whole run, never one per failed file, listing at most MAX_LISTED_FAILURES
 * names and folding the rest into an "and N more" suffix. It is extracted here
 * because a second batch surface now needs it — the presigned-slot request in
 * `useUploadSlots` — and two copies of the cap would be free to drift apart.
 *
 * The caller still owns the COPY (each surface names its own failure) and
 * therefore owns the two i18n keys; this module owns only the arithmetic.
 */

// How many failed file names the one acknowledgement spells out before it
// switches to a remainder count. The modal has no scroll container and is
// centred with translate(-50%, -50%), so an unbounded list would push its own
// dismiss button off-screen — which is worse than not naming every file.
export const MAX_LISTED_FAILURES = 5;

export interface ListedFailures {
  /** The listed names, already joined for interpolation into `{{names}}`. */
  names: string;
  /** How many further names were folded away; 0 when all of them are listed. */
  more: number;
}

/**
 * Split failed names into the listed head and the count of the elided tail.
 * An empty input yields `{ names: '', more: 0 }` — callers are expected to
 * check for failures before building a message, so this is not a special case
 * either of them has to handle.
 */
export const listFailedNames = (failedNames: string[]): ListedFailures => {
  const listed = failedNames.slice(0, MAX_LISTED_FAILURES);
  return {
    names: listed.join(', '),
    more: failedNames.length - listed.length,
  };
};
